import { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Popconfirm,
  Typography,
  Card,
  AutoComplete,
  Tooltip,
  Alert
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import customerService from '../services/customerService'

const { Title } = Typography
const { Option } = Select

function CustomersPage() {
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState(null)
  const [form] = Form.useForm()
  const [sosCustomerOptions, setSOSCustomerOptions] = useState([])
  const [sosCustomerSearching, setSOSCustomerSearching] = useState(false)
  const [pageSize, setPageSize] = useState(10)

  useEffect(() => {
    fetchCustomers()
  }, [])

  // Preload SOS customers so the dropdown is usable even before typing.
  useEffect(() => {
    if (modalVisible) {
      preloadSOSCustomers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalVisible])

  const fetchCustomers = async () => {
    setLoading(true)
    try {
      const data = await customerService.getAll()
      setCustomers(data)
    } catch (error) {
      message.error('Failed to load customers')
    } finally {
      setLoading(false)
    }
  }

  const preloadSOSCustomers = async () => {
    // If already loaded, don't refetch.
    if (sosCustomerOptions && sosCustomerOptions.length > 0) return

    setSOSCustomerSearching(true)
    try {
      // Backend returns all customers when query is empty.
      const data = await customerService.searchSOSCustomers('')
      const options = (data || []).map((customer) => ({
        value: customer.name || customer.fullname,
        label: customer.fullname || customer.name,
        sosCustomerId: customer.id,
        sosCustomerName: customer.name || customer.fullname
      }))
      setSOSCustomerOptions(options)
    } catch (error) {
      message.error('Failed to load SOS customers')
      setSOSCustomerOptions([])
    } finally {
      setSOSCustomerSearching(false)
    }
  }

  const handleSOSCustomerSelect = (value, option) => {
    // AutoComplete passes option directly; Select may pass an option object too.
    if (!option) return
    const opt = Array.isArray(option) ? option[0] : option
    form.setFieldsValue({
      sosCustomerName: opt.sosCustomerName || value,
      sosCustomerId: opt.sosCustomerId
    })
  }

  const handleAdd = () => {
    setEditingCustomer(null)
    form.resetFields()
    form.setFieldsValue({
      unitPriceSource: 'FromPO',
      defaultUnitPrice: 0,
      isActive: true
    })
    setSOSCustomerOptions([])
    setModalVisible(true)
  }

  const handleEdit = (record) => {
    setEditingCustomer(record)
    // If Default pricing is selected but defaultUnitPrice is missing, show 0 in the UI.
    const safeRecord = { ...record }
    if (
      safeRecord?.unitPriceSource === 'Default' &&
      (safeRecord.defaultUnitPrice === null || safeRecord.defaultUnitPrice === undefined || safeRecord.defaultUnitPrice === '')
    ) {
      safeRecord.defaultUnitPrice = 0
    }
    form.setFieldsValue(safeRecord)
    setModalVisible(true)
  }

  const handleDelete = async (id) => {
    try {
      await customerService.delete(id)
      message.success('Customer deleted successfully')
      fetchCustomers()
    } catch (error) {
      message.error('Failed to delete customer')
    }
  }

  const handleSubmit = async () => {
    try {
      const rawValues = await form.validateFields()

      // Important: send only meaningful values so backend defaults can apply.
      // Antd Form will include empty strings for untouched inputs; if we send those,
      // DynamoDB will store empty values instead of defaults like "[PO Number]".
      const values = { ...rawValues }
      Object.keys(values).forEach((k) => {
        const v = values[k]
        if (typeof v === 'string' && v.trim() === '') delete values[k]
        if (v === null || v === undefined) delete values[k]
      })

      // If using Default pricing, always send a numeric defaultUnitPrice.
      // If user left it empty, default to 0 automatically.
      if (values.unitPriceSource === 'Default') {
        const v = values.defaultUnitPrice
        if (v === undefined || v === null || v === '') {
          values.defaultUnitPrice = 0
        }
      }

      if (editingCustomer) {
        await customerService.update(editingCustomer.customerId, values)
        message.success('Customer updated successfully')
      } else {
        await customerService.create(values)
        message.success('Customer created successfully')
      }
      setModalVisible(false)
      setSOSCustomerOptions([])
      fetchCustomers()
    } catch (error) {
      if (error.errorFields) {
        return
      }
      message.error('Failed to save customer')
    }
  }

  const columns = [
    {
      title: 'Customer Name',
      dataIndex: 'customerName',
      key: 'customerName'
    },
    {
      title: 'SOS Customer Name',
      dataIndex: 'sosCustomerName',
      key: 'sosCustomerName'
    },
    {
      title: 'Email Domain',
      dataIndex: 'emailDomain',
      key: 'emailDomain'
    },
    {
      title: 'Order Number Formula',
      dataIndex: 'orderNumberFormula',
      key: 'orderNumberFormula'
    },
    {
      title: 'Unit Price Source',
      dataIndex: 'unitPriceSource',
      key: 'unitPriceSource'
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (isActive ? 'Active' : 'Inactive')
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title={`Delete customer "${record.customerName}"?`}
            description="This will remove the customer profile. It does not automatically delete related item mappings or past transactions."
            onConfirm={() => handleDelete(record.customerId)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}
        >
          <Title level={2} style={{ margin: 0 }}>
            Customers
          </Title>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Customer
          </Button>
        </div>

        <Card bodyStyle={{ padding: 0 }}>
          <Table
            dataSource={customers}
            columns={columns}
            rowKey={(record) => record.customerId}
            loading={loading}
            onRow={(record) => ({
              onDoubleClick: () => handleEdit(record)
            })}
            pagination={{ 
              pageSize: pageSize, 
              size: 'small',
              showSizeChanger: true,
              onShowSizeChange: (current, size) => setPageSize(size),
              pageSizeOptions: ['10', '20', '50', '100']
            }}
            scroll={{ x: 'max-content' }}
          />
        </Card>
      </Space>

      <Modal
        title={editingCustomer ? 'Edit Customer' : 'Add Customer'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false)
          setSOSCustomerOptions([])
        }}
        width={window.innerWidth < 768 ? '95%' : 600}
        style={{ top: 20 }}
      >
        <Alert
          message="Customer Setup Instructions"
          description={
            <ul style={{ paddingLeft: '20px', marginBottom: 0 }}>
              <li><strong>What this does:</strong> Routes incoming PO emails → identifies the customer → maps to the correct <strong>SOS Customer</strong> → creates the Sales Order.</li>
              <li><strong>Required fields:</strong> Customer Name, SOS Customer Name.</li>
              <li><strong>Recommended:</strong> Email Domain (or Email Address Pattern) so the system can automatically match incoming emails to this customer.</li>
              <li><strong>Optional:</strong> Order/PO formulas (defaults to <code>[PO Number]</code>), Unit Price Source (defaults to “From PO”), Default Unit Price (only if Unit Price Source is “Default”).</li>
              <li><strong>Formulas:</strong> Use <code>[PO Number]</code> to inject the extracted PO number (example: <code>HPL-[PO Number]</code>).</li>
            </ul>
          }
          type="info"
          showIcon
          icon={<QuestionCircleOutlined />}
          style={{ marginBottom: '20px' }}
        />

        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item
            name="customerName"
            label={
              <Space>
                Customer Name (Internal)
                <Tooltip title="Required. A friendly name used only inside this dashboard (e.g., 'El Paso').">
                  <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true, message: 'Please enter customer name' }]}
          >
            <Input placeholder="e.g., El Paso" />
          </Form.Item>

          <Form.Item
            name="sosCustomerName"
            label={
              <Space>
                SOS Customer Name
                <Tooltip title="Required. The customer record in SOS that Sales Orders will be created under. Type to search and select.">
                  <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true, message: 'Please select SOS customer name' }]}
          >
            <Select
              showSearch
              allowClear
              placeholder="Select SOS customer..."
              options={sosCustomerOptions}
              loading={sosCustomerSearching}
              onDropdownVisibleChange={(open) => {
                if (open) preloadSOSCustomers()
              }}
              onSelect={handleSOSCustomerSelect}
              filterOption={(input, option) =>
                (option?.label || '').toLowerCase().includes((input || '').toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item name="sosCustomerId" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="emailDomain"
            label={
              <Space>
                Email Domain
                <Tooltip title="Optional (recommended). Used to match the sender email domain (e.g., 'fmi.com'). If empty, use Email Address Pattern instead.">
                  <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                </Tooltip>
              </Space>
            }
          >
            <Input placeholder="e.g., fmi.com" />
          </Form.Item>

          <Form.Item 
            name="emailAddressPattern" 
            label={
              <Space>
                Email Address Pattern
                <Tooltip title="Optional. Extra matching rule (e.g., 'purchasing@' or 'orders@vendor.com'). Useful if multiple customers share the same domain.">
                  <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                </Tooltip>
              </Space>
            }
          >
            <Input placeholder="e.g., purchasing@hpl.com (Optional)" />
          </Form.Item>

          <Form.Item
            name="orderNumberFormula"
            label={
              <Space>
                Order Number Formula
                <Tooltip title="Optional. Controls SOS Sales Order 'Number'. Defaults to '[PO Number]'.">
                  <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                </Tooltip>
              </Space>
            }
          >
            <Input placeholder="e.g., [PO Number] or HPL[PO Number]" />
          </Form.Item>

          <Form.Item
            name="customerPOFormula"
            label={
              <Space>
                Customer PO Formula
                <Tooltip title="Optional. Controls SOS Sales Order 'Customer PO' field. Defaults to '[PO Number]'.">
                  <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                </Tooltip>
              </Space>
            }
          >
            <Input placeholder="e.g., [PO Number]" />
          </Form.Item>

          <Form.Item
            name="unitPriceSource"
            label={
              <Space>
                Unit Price Source
                <Tooltip title="Optional. Defaults to 'From PO'. 'Default' forces a fixed unit price for all items.">
                  <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                </Tooltip>
              </Space>
            }
          >
            <Select
              onChange={(val) => {
                if (val === 'Default') {
                  const curr = form.getFieldValue('defaultUnitPrice')
                  if (curr === undefined || curr === null || curr === '') {
                    form.setFieldsValue({ defaultUnitPrice: 0 })
                  }
                }
              }}
            >
              <Option value="FromPO">From PO (Recommended)</Option>
              <Option value="Default">Use Default Price</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="defaultUnitPrice"
            label={
              <Space>
                Default Unit Price
                <Tooltip title="Only required if Unit Price Source is 'Default'. This value will be used as the unit price for mapped items.">
                  <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                </Tooltip>
              </Space>
            }
            dependencies={['unitPriceSource']}
          >
            <Input type="number" placeholder="e.g., 1.00" />
          </Form.Item>

          <Form.Item 
            name="isActive" 
            label={
              <Space>
                Is Active
                <Tooltip title="If disabled, this customer profile will be ignored during email processing.">
                  <InfoCircleOutlined style={{ color: 'rgba(0,0,0,0.45)' }} />
                </Tooltip>
              </Space>
            }
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default CustomersPage
